import { PLACEHOLDER_HTML, showPopup, showPopupAsync, hidePopup } from '../popup-core.js';
import { addDefaultDialogPopupListeners } from '../popup.js';
import { logError } from '../datadog-logger.js';
import { createFeedbackDialog } from './feedback-dialog-module.js';

const ELEMENT_NAME = 'sheet-overlay';
const CONTENT_SELECTOR = '.sheet__content';
const SWIPE_CLOSE_THRESHOLD_PX = 110;
const SWIPE_SETTLE_TIMEOUT_MS = 450;

/**
 * @param {HTMLDialogElement} dialog
 */
function clearDragStyles(dialog) {
  delete dialog.dataset.dragging;
  dialog.style.transform = '';
  dialog.style.removeProperty('--sheet-scrim-opacity');
}

/**
 * @param {HTMLElement} element
 */
function forceReflow(element) {
  void element.offsetHeight;
}

/**
 * @param {() => void} callback
 * @returns {() => void}
 */
function once(callback) {
  let called = false;
  return () => {
    if (called) {
      return;
    }
    called = true;
    callback();
  };
}

/**
 * @param {HTMLDialogElement} dialog
 * @param {() => void} callback
 */
function afterSettle(dialog, callback) {
  const done = once(() => {
    dialog.removeEventListener('transitionend', onTransitionEnd);
    callback();
  });
  const onTransitionEnd = (event) => {
    if (event.target === dialog && event.propertyName === 'transform') {
      done();
    }
  };
  dialog.addEventListener('transitionend', onTransitionEnd);
  setTimeout(done, SWIPE_SETTLE_TIMEOUT_MS);
}

/**
 * @param {Error} error
 * @param {{logMessage: string, context: Record<string, unknown>, fallbackText: string}} loadError
 */
function reportLoadError(error, { logMessage, context, fallbackText }) {
  logError(logMessage, context, error);
  createFeedbackDialog('feedback_error', error.userMessage ?? fallbackText);
}

class SheetOverlay extends HTMLElement {
  /** @type {AbortController} */
  #listeners;
  /** @type {{key: string, html: string}|null} */
  #cachedContent = null;
  /** @type {number|null} */
  #dragPointerId = null;

  connectedCallback() {
    this.#listeners = new AbortController();

    this.addEventListener('pointerdown', (event) => this.#onPointerDown(event), { signal: this.#listeners.signal });
  }

  disconnectedCallback() {
    this.#listeners.abort();
  }

  /**
   * @param {Object} [options]
   */
  open({ content = null } = {}) {
    showPopup(content, this.#dialog.id, CONTENT_SELECTOR);
  }

  close() {
    hidePopup(this.#dialog.id);
  }

  /**
   * @param {(signal: AbortSignal) => Promise<string>} loadHtml
   * @param {Object} options
   */
  openAsync(loadHtml, { cacheKey = null, onContentSet = null, loadError }) {
    if (loadError === null || loadError === undefined) {
      throw new TypeError('loadError is required for an asynchronous sheet.');
    }

    const cachedHtml = cacheKey !== null && this.#cachedContent?.key === cacheKey ? this.#cachedContent.html : null;
    const load = cachedHtml === null ? loadHtml : () => Promise.resolve(cachedHtml);

    const onError = (error) => reportLoadError(error, loadError);
    const placeholder = this.querySelector('.js_sheet_loading_head').innerHTML + PLACEHOLDER_HTML;
    showPopupAsync(this.#dialog.id, load, { contentSelector: CONTENT_SELECTOR, placeholder, onError }).then(
      (content) => {
        if (content === null) {
          return;
        }
        if (cacheKey !== null) {
          this.#cachedContent = { key: cacheKey, html: content };
        }
        try {
          onContentSet?.(this);
        } catch (error) {
          logError('Sheet content wiring failed', { popupId: this.#dialog?.id }, error);
        }
      },
    );
  }

  /**
   * @returns {HTMLDialogElement}
   */
  get #dialog() {
    return this.querySelector('dialog');
  }

  /**
   * @param {PointerEvent} event
   */
  #onPointerDown(event) {
    const grabber = event.target instanceof Element ? event.target.closest('.js_sheet_grabber') : null;
    if (grabber === null || this.#dragPointerId !== null) {
      return;
    }

    const dialog = this.#dialog;
    const startY = event.clientY;
    const dialogHeight = dialog.offsetHeight;
    let delta = 0;

    event.preventDefault();
    grabber.setPointerCapture(event.pointerId);
    this.#dragPointerId = event.pointerId;
    dialog.dataset.dragging = 'true';

    const onMove = (moveEvent) => {
      delta = Math.max(0, moveEvent.clientY - startY);
      dialog.style.transform = `translateY(${delta}px)`;
      dialog.style.setProperty('--sheet-scrim-opacity', String(Math.max(0, 1 - delta / dialogHeight)));
    };

    const onUp = () => {
      this.#dragPointerId = null;
      grabber.removeEventListener('pointermove', onMove);
      grabber.removeEventListener('pointerup', onUp);
      grabber.removeEventListener('pointercancel', onUp);

      delete dialog.dataset.dragging;
      forceReflow(dialog);

      if (delta > SWIPE_CLOSE_THRESHOLD_PX) {
        afterSettle(dialog, () => {
          this.close();
          clearDragStyles(dialog);
        });
        dialog.style.transform = 'translateY(100%)';
        dialog.style.setProperty('--sheet-scrim-opacity', '0');
      } else {
        afterSettle(dialog, () => clearDragStyles(dialog));
        dialog.style.transform = 'none';
        dialog.style.removeProperty('--sheet-scrim-opacity');
      }
    };

    grabber.addEventListener('pointermove', onMove);
    grabber.addEventListener('pointerup', onUp);
    grabber.addEventListener('pointercancel', onUp);
  }
}

/**
 * @param {string} id The id of the sheet's dialog element.
 * @returns {SheetOverlay|null}
 */
function getSheet(id) {
  const sheet = document.getElementById(id)?.closest(ELEMENT_NAME) ?? null;
  if (sheet === null) {
    logError(`Unknown sheet id: ${id}`);
  }

  return sheet;
}

// Every consumer entry bundles its own copy of this module, so the first one to load
// defines the element and registers the shared dialog listeners.
if (customElements.get(ELEMENT_NAME) === undefined) {
  customElements.define(ELEMENT_NAME, SheetOverlay);
  addDefaultDialogPopupListeners();
}

export { getSheet };
